// src/components/Control/Operator/LaserWalls.tsx
// «Стены вокруг робота» (Настройки → Лазер и рулетка): схема сверху по лидару — точки,
// найденные стены, углы, цель — с приближением (колесо, кнопки), перетаскиванием и
// фиксацией картинки. Стены раскрашены по тому, где встанет робот, чтобы навести луч на
// это место (робот перебирает места: 0,6–3 м от стены, прямо напротив или до 40° вбок;
// под корпусом свободно, путь луча там, где он ниже 0,9 м, чист, пол виден лидару —
// laser_aim.stand_spot): зелёный — прямо напротив на нужном расстоянии, оранжевый — под
// углом или ближе нужного, красный — встать негде, серый — у стены не видно угла.
// Клик по стене показывает место стоянки, круг корпуса и путь луча, а если встать негде —
// мешающие точки; их можно отметить «не препятствие» (низкая мебель, провод, отражение):
// робот хранит такие отметки на карте и перестаёт их учитывать.
// Данные — robot/<id>/laser/state.walls: points, lines, reach[i] {corners, spots[[status, x, y, d, ang]],
// step}, reach_height, pivot_z, body_r, ignore; команды в robot/<id>/laser/cmd: approach, ignore,
// unignore, config {walls_sens, reach_height}.
import React, { useEffect, useRef, useState } from 'react';
import { LaserSide, LaserState, LaserWalls } from '../robot/telemetry';

export type Pt = [number, number];
type Box = { minU: number; maxU: number; minW: number; maxW: number };
type View = { zoom: number; cu: number; cw: number } | null;   // null — вписать всё
type Status = 'ok' | 'short' | 'bad' | 'nocorner';

const W = 900, H = 540;
const STATUS_TEXT: Record<Status, string> = {
  ok: 'можно подъехать', short: 'встанет не идеально', bad: 'роботу негде встать', nocorner: 'не видно угла',
};
const CODE: Status[] = ['bad', 'ok', 'short'];

const num = (v: number, digits = 1) => v.toLocaleString('ru-RU', { maximumFractionDigits: digits, minimumFractionDigits: digits });
const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// Точка на ломаной в `offset` м от её начала (по длине); дальше конца — по последнему отрезку
function alongPolyline(poly: Pt[], offset: number): Pt | null {
  if (poly.length < 2) return null;
  let left = offset;
  for (let i = 1; i < poly.length; i++) {
    const d = dist(poly[i - 1], poly[i]);
    if (left <= d || i === poly.length - 1) {
      const k = d > 0 ? left / d : 0;
      return [poly[i - 1][0] + (poly[i][0] - poly[i - 1][0]) * k, poly[i - 1][1] + (poly[i][1] - poly[i - 1][1]) * k];
    }
    left -= d;
  }
  return null;
}

// Цель до наведения (как laser_aim.target_point): от угла вдоль стены; у изогнутой — по её профилю
export function previewTarget(w: LaserWalls, side: LaserSide, offset: number): Pt | null {
  const c = w.corners[side] as Pt | null;
  if (!w.front || !c) return null;
  if (w.front.kind === 'curved' && w.front.profile && w.front.profile.length > 1) {
    const prof = w.front.profile as Pt[];
    const fromCorner = dist(prof[0], c) < dist(prof[prof.length - 1], c) ? prof : [...prof].reverse();
    return alongPolyline([c, ...fromCorner], offset);
  }
  const [x0, y0, x1, y1] = w.front.line;
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  let ux = (x1 - x0) / len, uy = (y1 - y0) / len;
  if ((c[0] - (x0 + x1) / 2) * ux + (c[1] - (y0 + y1) / 2) * uy < 0) { ux = -ux; uy = -uy; }
  return [c[0] - offset * ux, c[1] - offset * uy];
}

// Линия стены: концы, направление, нормаль к роботу
function geom(l: number[]) {
  const a: Pt = [l[0], l[1]], b: Pt = [l[2], l[3]];
  const len = dist(a, b) || 1e-9;
  const ux = (b[0] - a[0]) / len, uy = (b[1] - a[1]) / len;
  let nx = -uy, ny = ux;
  if (-(a[0] * nx + a[1] * ny) < 0) { nx = -nx; ny = -ny; }   // к роботу (в начало координат)
  return { a, b, len, ux, uy, nx, ny };
}

export interface PickInfo {
  point: Pt;             // место на стене
  n: Pt;                 // нормаль стены к комнате
  status: Status;
  stand: Pt | null;      // где встанет робот
  d: number;             // от места стоянки до точки, м
  ang: number;           // вбок от перпендикуляра, °
  tilt: number;          // наклон камеры к цели, °
}

// Разбор клика: ближайшая стена не дальше 0,4 м и где встанет робот (по расчёту робота)
function analyse(w: LaserWalls, p: Pt, height: number): PickInfo | null {
  let best: { d: number; i: number; t: number; F: Pt } | null = null;
  w.lines.forEach((l, i) => {
    const g = geom(l);
    const t = Math.max(0, Math.min(g.len, (p[0] - g.a[0]) * g.ux + (p[1] - g.a[1]) * g.uy));
    const F: Pt = [g.a[0] + g.ux * t, g.a[1] + g.uy * t];
    const d = dist(F, p);
    if (d <= 0.4 && (!best || d < best.d)) best = { d, i, t, F };
  });
  if (!best) return null;
  const { i, t, F } = best as { d: number; i: number; t: number; F: Pt };
  const g = geom(w.lines[i]);
  const r = w.reach?.[i];
  const base = { point: F, n: [g.nx, g.ny] as Pt, stand: null, d: 0, ang: 0, tilt: 0 };
  if (!r) return { ...base, status: 'ok' };
  if (!r.spots.length) return { ...base, status: 'nocorner' };
  const sp = r.spots[Math.min(r.spots.length - 1, Math.max(0, Math.round(t / r.step)))];
  const status = CODE[sp[0]] ?? 'bad';
  if (status === 'bad') return { ...base, status };
  const d = sp[3], pivot = w.pivot_z ?? 0.19;
  return { point: F, n: [g.nx, g.ny], status, stand: [sp[1], sp[2]], d, ang: sp[4],
           tilt: (Math.atan2(height - pivot, d) * 180) / Math.PI };
}

// Точки лидара, которые мешают встать перед местом F: в 0,15–1,8 м перед стеной, ±0,7 м вбок
function blockersAt(points: Pt[], F: Pt, n: Pt): Pt[] {
  const [nx, ny] = n, lx = -ny, ly = nx;
  return points.filter(p => {
    const along = (p[0] - F[0]) * nx + (p[1] - F[1]) * ny, across = Math.abs((p[0] - F[0]) * lx + (p[1] - F[1]) * ly);
    return along > 0.15 && along < 1.8 && across < 0.7;
  });
}

// Границы «вписать всё» меняются, только если картинка заметно не влезает или стала мелкой
function useStableBox(pts: Pt[]): Box {
  const ref = useRef<Box | null>(null);
  const us = pts.map(p => -p[1]), ws = pts.map(p => -p[0]);
  const want: Box = { minU: Math.min(...us), maxU: Math.max(...us), minW: Math.min(...ws), maxW: Math.max(...ws) };
  const cur = ref.current;
  const size = (b: Box) => Math.max(b.maxU - b.minU, b.maxW - b.minW, 0.5);
  if (!cur || want.minU < cur.minU || want.maxU > cur.maxU || want.minW < cur.minW || want.maxW > cur.maxW
      || size(want) < size(cur) * 0.6) {
    const m = size(want) * 0.06;
    ref.current = { minU: want.minU - m, maxU: want.maxU + m, minW: want.minW - m, maxW: want.maxW + m };
  }
  return ref.current!;
}

const NoScan: React.FC<{ why: string }> = ({ why }) => (
  <figure className="op-laser-fig">
    <svg viewBox={`0 0 ${W} ${H}`} className="op-walls-svg empty" role="img" aria-label={`Схема сверху: ${why}`}>
      <path d={`M${W / 2},${H / 2 - 9} l7,14 h-14 z`} className="op-laser-robot" />
      <text x={W / 2} y={H / 2 + 40} textAnchor="middle" className="op-laser-note">Нет данных лидара</text>
    </svg>
    <figcaption className="op-laser-note-line">Схема пустая: {why}.</figcaption>
  </figure>
);

export const WallsCard: React.FC<{
  st: LaserState | null;
  at: number;
  height: number;                         // м, из формы
  side: LaserSide;
  offset: number;                         // м, из формы — для цели «до наведения»
  noScanWhy: string;
  blocked: string | null;                 // почему сейчас нельзя ехать (нода, сон…)
  onApproach: (point: Pt) => void;
  send: (m: object) => void;
}> = ({ st, at, height, side, offset, noScanWhy, blocked, onApproach, send }) => {
  const [frozen, setFrozen] = useState<{ walls: LaserWalls; at: number } | null>(null);
  const [pick, setPick] = useState<Pt | null>(null);
  const [missed, setMissed] = useState(false);
  const [marking, setMarking] = useState(false);      // режим «отметить: не препятствие»
  const [view, setView] = useState<View>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean; view: { zoom: number; cu: number; cw: number } } | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;

  // Цвета «можно подъехать» считаются на роботе для высоты цели: сообщаем её (с задержкой)
  const reachHeight = st?.walls?.reach_height;
  const online = !!st;
  useEffect(() => {
    if (!online || !Number.isFinite(height) || (reachHeight != null && Math.abs(reachHeight - height) < 0.01)) return undefined;
    const t = setTimeout(() => sendRef.current({ cmd: 'config', reach_height: Math.round(height * 1000) / 1000 }), 700);
    return () => clearTimeout(t);
  }, [height, reachHeight, online]);

  const w = frozen?.walls ?? st?.walls ?? null;
  const res = st?.result;
  const near = (p: number[]) => Math.hypot(p[0], p[1]) < 7;
  const points = (w?.points ?? []).filter(near) as Pt[];
  const lines = w?.lines ?? [];
  const all: Pt[] = [[0, 0], ...points, ...lines.flatMap(l => [[l[0], l[1]], [l[2], l[3]]] as Pt[]).filter(near)];
  const box = useStableBox(all);
  const fit = Math.min(W / (box.maxU - box.minU), H / (box.maxW - box.minW));
  const cur = view ?? { zoom: 1, cu: (box.minU + box.maxU) / 2, cw: (box.minW + box.maxW) / 2 };
  const s = fit * cur.zoom;
  const X = (p: Pt) => W / 2 + (-p[1] - cur.cu) * s;
  const Y = (p: Pt) => H / 2 + (-p[0] - cur.cw) * s;
  const toBase = (sx: number, sy: number): Pt => {
    const u = (sx - W / 2) / s + cur.cu, ww = (sy - H / 2) / s + cur.cw;
    return [-ww, -u];
  };
  const svgXY = (e: { clientX: number; clientY: number }) => {
    const r = svgRef.current!.getBoundingClientRect();
    return [((e.clientX - r.left) * W) / r.width, ((e.clientY - r.top) * H) / r.height];
  };

  const zoomAt = (factor: number, sx = W / 2, sy = H / 2) => {
    setView(v => {
      const c = v ?? cur;
      const zoom = Math.min(16, Math.max(1, c.zoom * factor));
      if (zoom === 1 && factor < 1) return null;
      const s0 = fit * c.zoom, s1 = fit * zoom;
      const u = (sx - W / 2) / s0 + c.cu, ww = (sy - H / 2) / s0 + c.cw;     // точка под курсором остаётся на месте
      return { zoom, cu: u - (sx - W / 2) / s1, cw: ww - (sy - H / 2) / s1 };
    });
  };

  // Колесо: приближать, а не прокручивать страницу (нужен не-passive обработчик)
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const [sx, sy] = svgXY(e);
      zoomAt(e.deltaY < 0 ? 1.2 : 1 / 1.2, sx, sy);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const ignore = (pts: Pt[], r = 0.12) => {
    if (pts.length) send({ cmd: 'ignore', points: pts.map(p => [Math.round(p[0] * 1000) / 1000, Math.round(p[1] * 1000) / 1000]), r });
  };

  const onDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const [x, y] = svgXY(e);
    drag.current = { x, y, moved: false, view: cur };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    const [x, y] = svgXY(e);
    if (!d.moved && Math.hypot(x - d.x, y - d.y) < 6) return;
    d.moved = true;
    const sc = fit * d.view.zoom;
    setView({ zoom: d.view.zoom, cu: d.view.cu - (x - d.x) / sc, cw: d.view.cw - (y - d.y) / sc });
  };
  const onUp = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.moved || !w) return;
    const [x, y] = svgXY(e);
    const p = toBase(x, y);
    if (marking) {             // отметить точки лидара у клика «не препятствие»
      ignore(points.filter(q => dist(q, p) < Math.max(0.12, 14 / s)));
      return;
    }
    const info = analyse(w, p, height);
    setMissed(!info);
    setPick(info ? info.point : null);
    if (info && !frozen && st?.walls) setFrozen({ walls: st.walls, at });   // выбрали — картинка замирает
  };

  if (!st) return null;
  const info = pick && w ? analyse(w, pick, height) : null;
  const age = Math.max(0, Math.round((Date.now() - (frozen?.at ?? at)) / 1000));
  const cancel = () => { setPick(null); setFrozen(null); };
  const ignored = w?.ignore ?? [];
  const stale = !!w && w.reach_height != null && Math.abs(w.reach_height - height) >= 0.01;

  let sketch: React.ReactNode;
  if (!w || points.length === 0) {
    sketch = <NoScan why={noScanWhy} />;
  } else {
    const front = w.front;
    const profile = (front?.kind === 'curved' ? front.profile : null) as Pt[] | null;
    const corner = res ? (res.model.corner as Pt) : (w.corners[side] as Pt | null) ?? null;
    const target: Pt | null = pick ? null : res ? [res.point[0], res.point[1]] : previewTarget(w, side, offset);
    const path = (poly: Pt[]) => poly.map((p, i) => `${i ? 'L' : 'M'}${X(p).toFixed(1)},${Y(p).toFixed(1)}`).join('');
    // стены по участкам: где встанет робот для этого места
    const segs: { a: Pt; b: Pt; st: Status }[] = [];
    lines.forEach((l, i) => {
      const r = w.reach?.[i];
      const g = geom(l);
      if (!r) { segs.push({ a: g.a, b: g.b, st: 'ok' }); return; }
      if (!r.spots.length) { segs.push({ a: g.a, b: g.b, st: 'nocorner' }); return; }
      r.spots.forEach((sp, k) => {
        const t0 = Math.max(0, (k - 0.5) * r.step), t1 = Math.min(g.len, (k + 0.5) * r.step);
        if (t1 > t0) segs.push({ a: [g.a[0] + g.ux * t0, g.a[1] + g.uy * t0], b: [g.a[0] + g.ux * t1, g.a[1] + g.uy * t1], st: CODE[sp[0]] ?? 'bad' });
      });
    });
    let pickLayer: React.ReactNode = null;
    if (info) {
      const F = info.point;
      const body = w.body_r ?? 0.22;
      const blockers = info.status === 'bad' ? blockersAt(points, F, info.n) : [];
      // значок «вверх» повернуть к точке: экранное направление (−dy, −dx) базового вектора к точке
      const rot = info.stand
        ? (Math.atan2(-(F[1] - info.stand[1]), (F[0] - info.stand[0])) * 180) / Math.PI
        : 0;
      pickLayer = (
        <g className={`op-walls-pick ${info.status}`}>
          {info.status === 'bad' && (() => {
            const [nx, ny] = info.n, lx = -ny, ly = nx;
            const c: Pt[] = [[F[0] + lx * 0.7, F[1] + ly * 0.7], [F[0] - lx * 0.7, F[1] - ly * 0.7],
              [F[0] - lx * 0.7 + nx * 1.8, F[1] - ly * 0.7 + ny * 1.8], [F[0] + lx * 0.7 + nx * 1.8, F[1] + ly * 0.7 + ny * 1.8]];
            return <path d={path(c) + 'Z'} className="corridor" />;
          })()}
          {blockers.map((p, i) => <circle key={i} cx={X(p)} cy={Y(p)} r={3.2} className="blocker" />)}
          {info.stand && (
            <>
              <line x1={X(info.stand)} y1={Y(info.stand)} x2={X(F)} y2={Y(F)} className="beam" />
              <circle cx={X(info.stand)} cy={Y(info.stand)} r={body * s} className="body" />
              <g transform={`translate(${X(info.stand)},${Y(info.stand)}) rotate(${rot})`}>
                <path d="M0,-11 l8,16 h-16 z" className="stand" />
              </g>
            </>
          )}
          <path d={`M${X(F)},${Y(F) - 9}l9,9l-9,9l-9,-9z`} className="spot" />
        </g>
      );
    }
    sketch = (
      <figure className="op-laser-fig">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className={`op-walls-svg ${marking ? 'marking' : ''}`} role="img"
             aria-label="Схема сверху: стены по лидару; клик по стене — подъехать и навести туда"
             onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
             onPointerCancel={() => { drag.current = null; }}>
          {ignored.map(([x, y, r], i) => <circle key={i} cx={X([x, y])} cy={Y([x, y])} r={Math.max(3, r * s)} className="op-walls-ignored" />)}
          {points.map((p, i) => <circle key={i} cx={X(p)} cy={Y(p)} r={marking ? 2.2 : 1.3} className="op-laser-pt" />)}
          {front && (profile
            ? <path d={path(profile)} className="op-walls-front" fill="none" />
            : <line x1={X([front.line[0], front.line[1]])} y1={Y([front.line[0], front.line[1]])}
                    x2={X([front.line[2], front.line[3]])} y2={Y([front.line[2], front.line[3]])} className="op-walls-front" />)}
          {segs.map((g, i) => <line key={i} x1={X(g.a)} y1={Y(g.a)} x2={X(g.b)} y2={Y(g.b)} className={`op-walls-seg ${g.st}`} />)}
          {(['left', 'right'] as const).map(k => {
            const c = (res ? (k === side ? res.model.corner : null) : w.corners[k]) as Pt | null;
            return c ? (
              <g key={k}>
                <circle cx={X(c)} cy={Y(c)} r={5} className={`op-laser-corner ${k === side ? 'on' : ''}`} />
                <text x={X(c) + (k === 'left' ? -9 : 9)} y={Y(c) - 8} textAnchor={k === 'left' ? 'end' : 'start'}
                      className="op-laser-note">{k === 'left' ? 'левый угол' : 'правый угол'}</text>
              </g>
            ) : null;
          })}
          {corner && target && <line x1={X(corner)} y1={Y(corner)} x2={X(target)} y2={Y(target)} className="op-laser-dim" />}
          {target && st.laser && <line x1={X([0, 0])} y1={Y([0, 0])} x2={X(target)} y2={Y(target)} className="op-laser-beam" />}
          {target && (
            <g className={res ? 'op-laser-target on' : 'op-laser-target'}>
              <circle cx={X(target)} cy={Y(target)} r={7} />
              <path d={`M${X(target) - 10},${Y(target)}h20M${X(target)},${Y(target) - 10}v20`} />
            </g>
          )}
          {pickLayer}
          <path d={`M${X([0, 0])},${Y([0, 0]) - 10} l8,16 h-16 z`} className="op-laser-robot" />
        </svg>
        <figcaption className="op-laser-note-line">
          {front
            ? <>Стена впереди: {num(front.dist, 2)} м, робот повёрнут {front.angle_deg >= 0 ? 'влево' : 'вправо'} на{' '}
              {num(Math.abs(front.angle_deg), 0)}°{front.kind === 'curved'
                ? <> · <b>изогнута</b>, отклонение от прямой ~{num(Math.abs(front.sag_cm ?? 0), 0)} см</>
                : front.sag_cm != null ? ' · плоская' : ''}</>
            : 'Стены прямо перед роботом не видно.'}
          {' '}· {frozen ? <b>зафиксировано {age < 2 ? 'только что' : `${age} с назад`}</b>
            : <>{w.scans && w.scans > 1 ? `накоплено сканов: ${w.scans}` : 'один скан (робот едет)'} · {age < 2 ? 'только что' : `${age} с назад`}</>}
          {stale && <> · <b>пересчитываю цвета для высоты {num(height * 100, 0)} см…</b></>}
          <span className="op-laser-legend">
            <i className="pt" /> точки лидара <i className="seg ok" /> можно подъехать <i className="seg short" /> под углом / ближе
            <i className="seg bad" /> негде встать <i className="seg nocorner" /> не видно угла <i className="ign" /> не препятствие
          </span>
        </figcaption>
      </figure>
    );
  }

  let pickText: React.ReactNode = null;
  if (info) {
    const hcm = num(height * 100, 0);
    pickText = {
      ok: <>Робот встанет в {num(info.d, 2)} м прямо напротив точки и наведёт луч на высоту {hcm} см (камера вверх на {num(info.tilt, 0)}°).</>,
      short: info.ang
        ? <>Прямо напротив мешают предметы — робот встанет под углом {num(Math.abs(info.ang), 0)}° к стене в {num(info.d, 2)} м от точки
          (камера вверх на {num(info.tilt, 0)}°). Точность почти та же.</>
        : <>Дальше от стены встать не дают предметы — робот встанет в {num(info.d, 2)} м: наклон камеры к точке {num(info.tilt, 0)}°,
          круче обычного.</>,
      bad: <>Ни прямо, ни под углом до 40° роботу негде встать: под корпусом или на пути луча (пока он ниже 0,9 м) — точки,
        подсвеченные красным. Если это не препятствие (низкая мебель, провод, отражение) — отметьте их, и робот перестанет их учитывать.</>,
      nocorner: <>У этой стены лидар не видит угла с соседней стеной — не от чего отсчитать точку. Выберите стену с
        отмеченным углом или прибавьте «Поиск стен».</>,
    }[info.status];
  }
  const blockerPts = info?.status === 'bad' && w ? blockersAt(points, info.point, info.n) : [];

  return (
    <div className="feature-card op-walls-card">
      <h3><span>Стены вокруг робота</span></h3>
      <div className="row op-walls-tools">
        <button type="button" className="op-btn" onClick={() => zoomAt(1.4)} aria-label="Приблизить" title="Приблизить (колесо мыши)">+</button>
        <button type="button" className="op-btn" onClick={() => zoomAt(1 / 1.4)} aria-label="Отдалить" title="Отдалить">−</button>
        <button type="button" className="op-btn" onClick={() => setView(null)} disabled={!view}>Вписать</button>
        <button type="button" className={`op-btn ${frozen ? 'active' : ''}`} aria-pressed={!!frozen}
                onClick={() => (frozen ? cancel() : st.walls && setFrozen({ walls: st.walls, at }))}
                disabled={!frozen && !st.walls}
                title="Остановить обновление схемы, чтобы спокойно выбрать место">
          {frozen ? 'Обновлять' : 'Зафиксировать'}
        </button>
        <button type="button" className={`op-btn ${marking ? 'active' : ''}`} aria-pressed={marking}
                onClick={() => { setMarking(m => !m); cancel(); }}
                title="Кликайте по точкам лидара, которые на самом деле не мешают (провод, низкая мебель, отражение)">
          {marking ? 'Готово' : 'Отметить «не препятствие»'}
        </button>
        {ignored.length > 0 && (
          <button type="button" className="op-btn" onClick={() => send({ cmd: 'unignore' })}>
            Вернуть препятствия ({ignored.length})
          </button>
        )}
      </div>
      <p className="hint">{marking
        ? 'Режим отметки: кликайте по точкам лидара, которые не мешают роботу, — они станут серыми кругами и перестанут учитываться. «Готово» — назад к выбору места.'
        : 'Колесо — приблизить, перетаскивание — сдвинуть, клик по стене — выбрать место.'}</p>
      {sketch}
      {info && !marking ? (
        <div className={`op-laser-pickbar ${info.status}`}>
          <b>{STATUS_TEXT[info.status]}.</b> {pickText}
          <div className="row">
            {(info.status === 'ok' || info.status === 'short') && (
              <button type="button" className="op-btn primary" disabled={!!blocked || st.busy}
                      onClick={() => { onApproach(info.point); cancel(); }}>
                Подъехать и навести
              </button>
            )}
            {info.status === 'bad' && blockerPts.length > 0 && (
              <button type="button" className="op-btn" onClick={() => { ignore(blockerPts); cancel(); }}
                      title="Красные точки перестанут считаться препятствием (отметки хранятся на карте)">
                Не считать эти точки препятствием
              </button>
            )}
            <button type="button" className="op-btn" onClick={cancel}>Отменить</button>
          </div>
          {blocked && <p className="hint">{blocked}</p>}
        </div>
      ) : !marking && (
        <p className="hint">{missed ? 'Там нет найденной стены — кликните по линии стены (или прибавьте «Поиск стен»).'
          : `Кликните по стене — робот подъедет и наведёт луч туда на высоту ${num(height * 100, 0)} см (из формы ниже).`}</p>
      )}
      <WallsSens st={st} send={send} />
    </div>
  );
};

const WallsSens: React.FC<{ st: LaserState; send: (m: object) => void }> = ({ st, send }) => {
  const [drag, setDrag] = useState<number | null>(null);
  const value = drag ?? st.walls_sens ?? 1.5;
  const commit = () => {
    if (drag !== null) send({ cmd: 'config', walls_sens: drag });
    setDrag(null);
  };
  return (
    <div className="row op-laser-sens">
      <label htmlFor="laser-sens">Поиск стен</label>
      <input id="laser-sens" type="range" min={0.5} max={3} step={0.25} value={value}
             onChange={e => setDrag(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit} onBlur={commit} />
      <span className="hint">{num(value, 2)} · {value < 1.25 ? 'только длинные чистые стены' : value <= 2 ? 'обычно' : 'всё, что похоже на стену (и мебель)'}</span>
    </div>
  );
};
